import { isSoftPortalSwitchInGrace } from './portal';
import { getDbSupabase } from './supabase';

const VAPID_PUBLIC_KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '').trim();

/** Bump when SW behavior changes — triggers one-time unregister + cache purge. */
export const SW_CACHE_BUST = 'v9';
const SW_SCRIPT = `/sw.js?v=${SW_CACHE_BUST}`;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export function isWebPushConfigured(): boolean {
  return VAPID_PUBLIC_KEY.length > 0;
}

async function clearCacheStorage(): Promise<void> {
  if (!('caches' in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

/**
 * One-time: kill any old caching service worker that made soft-refresh show stale UI.
 * Hard refresh bypasses SW; soft refresh does not — so old SW must be removed.
 */
export async function migrateServiceWorkerIfNeeded(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;

  const key = 'bth_sw_cache_bust';
  let prev: string | null = null;
  try {
    prev = localStorage.getItem(key);
  } catch {
    prev = null;
  }
  if (prev === SW_CACHE_BUST) return false;

  let hadRegistration = false;
  try {
    await clearCacheStorage();
    const regs = await navigator.serviceWorker.getRegistrations();
    hadRegistration = regs.length > 0;
    await Promise.all(regs.map((reg) => reg.unregister()));
  } catch (err) {
    console.warn('[BTH] service worker migration failed', err);
  }

  try {
    localStorage.setItem(key, SW_CACHE_BUST);
  } catch {
    // ignore
  }

  // Hard refresh bypasses the controller, but registrations still exist — must reload
  // so the next soft refresh is not still controlled by the old worker.
  if (hadRegistration) {
    window.location.reload();
    return true;
  }
  return false;
}

/**
 * Register push SW without caching the app shell.
 * updateViaCache:'none' forces the browser to revalidate sw.js on each visit.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    await clearCacheStorage();
    const registration = await navigator.serviceWorker.register(SW_SCRIPT, {
      scope: '/',
      updateViaCache: 'none',
    });
    // Do not SKIP_WAITING / force-activate here — that reloads the open CRM tab
    // when a ticket push arrives or the page regains focus (Verifying flash).
    // sw.js install already calls skipWaiting for first install.
    return registration;
  } catch (err) {
    console.warn('[BTH] service worker registration failed', err);
    return null;
  }
}

/**
 * Keep push SW healthy without full-page reload.
 * Ticket push / focus used to SKIP_WAITING → controllerchange → reload → "Verifying access…".
 * One-time cache-bust reload stays in migrateServiceWorkerIfNeeded only.
 */
export function watchServiceWorkerUpdates(): void {
  if (!('serviceWorker' in navigator)) return;

  const check = () => {
    if (isSoftPortalSwitchInGrace()) return;
    void clearCacheStorage();
    // Soft update check only — never SKIP_WAITING here (that reloads the tab).
    void navigator.serviceWorker.getRegistration().then((reg) => {
      void reg?.update();
    });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
  window.addEventListener('focus', check);
}

/** Shared device subscription only — portal access is enforced when sending. */
export async function savePushSubscription(userId: string, subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;

  const { error } = await getDbSupabase()
    .schema('public')
    .from('portal_push_subscriptions')
    .upsert(
      {
        user_id: userId,
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth_key: json.keys.auth,
        user_agent: navigator.userAgent.slice(0, 500),
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,endpoint' },
    );

  if (error) {
    console.warn('[BTH] failed to save shared push subscription', error);
  }
}

export async function subscribeToWebPush(
  userId: string,
  options?: { /** When true, reuse existing SW only — no update/SKIP_WAITING. */ quiet?: boolean },
): Promise<boolean> {
  if (!isWebPushConfigured()) return false;
  if (!('PushManager' in window)) return false;

  let registration: ServiceWorkerRegistration | null = null;
  if (options?.quiet && 'serviceWorker' in navigator) {
    registration = (await navigator.serviceWorker.getRegistration()) ?? null;
    if (!registration) {
      // First-time only — still needed to subscribe.
      registration = await registerServiceWorker();
    }
  } else {
    registration = (await navigator.serviceWorker.ready) ?? (await registerServiceWorker());
  }
  if (!registration) return false;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  await savePushSubscription(userId, subscription);
  return true;
}

/**
 * Ensure push subscription exists. Quiet path avoids SW update/reload during
 * choose→portal (notifications-on was double-flashing Verifying on mobile CRM).
 */
export async function ensureWebPushSubscription(userId: string): Promise<void> {
  if (!isWebPushConfigured()) return;
  if (Notification.permission !== 'granted') return;
  if (isSoftPortalSwitchInGrace()) return;
  try {
    await subscribeToWebPush(userId, { quiet: true });
  } catch (err) {
    console.warn('[BTH] web push subscription failed', err);
  }
}
